import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADVANCE_CLOSURE_LABELS,
  AUDIT_ACTION_LABELS,
  type AuditEventResponse,
  canWriteOff,
  CLOSURE_REASON_LABELS,
  compensationError,
  compensationOutcome,
  compensationProposal,
  type CompensationRequest,
  creditLinkError,
  type CreditLinkRequest,
  formatMoney,
  formatNumber,
  fromCents,
  INVOICE_KIND_LABELS,
  type InvoiceResponse,
  type InvoiceXmlResponse,
  italianToday,
  parseDecimal,
  toCents,
  type TreasuryOption,
} from '@k-management/shared';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { ReclassPicker } from '../../components/ReclassPicker.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { GestifyConnectDialog } from '../../components/GestifyConnectDialog.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { useGestifyBrowser } from '../../lib/gestify-browser.js';
import { readInvoiceXml } from './gestify-xml.js';
import { InvoiceAdvanceSection } from './InvoiceAdvanceSection.js';
import { InvoiceBankPanel } from './InvoiceBankPanel.js';
import { InvoiceCancellation } from './InvoiceCancellation.js';
import { InvoiceSubjectPayments } from './SubjectPayments.js';
import { ManualAttachment, ProformaPanel } from './ProformaPanel.js';
import { ClosureDialog, useClosureDocument } from './ClosureDialog.js';
import {
  CreditNoteMark,
  creditNoteRow,
  InvoiceStatePill,
  movementPath,
  treasuryKeys,
} from './treasury-ui.js';
import { InvoiceJobsCard } from '../jobs/InvoiceJobsCard.js';
import { LineSplitDialog } from './LineSplitDialog.js';

/**
 * An invoice of the Tesoreria (06/10/2026): on the left what comes from
 * Gestify, locked; on the right what K-Rev adds; below the due dates, the
 * collections or payments that cover them in order, and the history.
 */
export function InvoicePage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const canWrite = useCan('tesoreria.write');
  const invoice = useQuery({
    queryKey: treasuryKeys.invoice(id),
    queryFn: () => apiRequest<InvoiceResponse>(`/treasury/invoices/${id}`),
  });
  const data = invoice.data;
  usePageTitle(data ? `${INVOICE_KIND_LABELS[data.kind]} ${data.number}` : 'Fattura');
  const [settling, setSettling] = useState(false);
  const [searching, setSearching] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [writingOff, setWritingOff] = useState(false);
  const [closing, setClosing] = useState(false);
  const canClose = useCan('tesoreria.closures');
  const closureDocument = useClosureDocument(id);
  const navigate = useNavigate();
  const removeDocument = useMutation({
    mutationFn: () => apiRequest<void>(`/treasury/invoices/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
      navigate('/tesoreria/fatture-passive', { replace: true });
    },
  });

  const refresh = async (updated: InvoiceResponse) => {
    queryClient.setQueryData(treasuryKeys.invoice(id), updated);
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.invoices, exact: true });
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.history(id) });
  };
  const remove = useMutation({
    mutationFn: (settlementId: string) =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${id}/settlements/${settlementId}`, {
        method: 'DELETE',
      }),
    onSuccess: async (updated) => {
      setRemoving(null);
      await refresh(updated);
    },
  });

  const writeOff = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${id}/write-off`, { method: 'POST' }),
    onSuccess: async (updated) => {
      setWritingOff(false);
      await refresh(updated);
    },
  });
  const dues = useTableSort('invoice.dues', data?.dues ?? [], {
    dueDate: (due) => due.dueDate,
    amount: (due) => amountSort(due.amount),
    settled: (due) => amountSort(due.settled),
    residual: (due) => amountSort(due.residual),
  });
  const settlements = useTableSort('invoice.settlements', data?.settlements ?? [], {
    date: (settlement) => settlement.date,
    amount: (settlement) => amountSort(settlement.amount),
    account: (settlement) =>
      settlement.accountCode
        ? (data?.options.accounts.find((option) => option.code === settlement.accountCode)?.label ??
          settlement.accountCode)
        : null,
  });
  const advances = useTableSort('invoice.advances', data?.advances ?? [], {
    date: (advance) => advance.date,
    bank: (advance) => advance.accountCode,
    advanced: (advance) => amountSort(advance.advanced),
    open: (advance) => amountSort(advance.open),
  });

  if (invoice.isPending) return <p className="muted">Caricamento…</p>;
  if (invoice.isError) return <p className="error">{invoice.error.message}</p>;
  const issued = data!.direction === 'emessa';
  const item = data!;
  const what = issued ? 'incasso' : 'pagamento';
  // An item of the situation at 1 January (1.48): no electronic invoice, no lines.
  const opening = item.origin === 'apertura';
  // Entered by hand (1.59): changed here, its PDF on SharePoint, no electronic invoice.
  const manual = item.origin === 'manuale';
  // Collected or paid through the bank from here (1.72): not credit notes or self-billing.
  const bankSearch =
    canWrite &&
    item.kind !== 'nota_credito' &&
    !item.selfBilling &&
    !item.replaced &&
    (Number(item.residual) > 0 || item.settlements.some((settlement) => settlement.manual));
  const accountLabel = (code: string | null) =>
    code ? (item.options.accounts.find((option) => option.code === code)?.label ?? code) : '—';

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to={issued ? '/tesoreria/fatture-attive' : '/tesoreria/fatture-passive'}>
              {issued ? 'Fatture attive' : 'Fatture passive'}
            </Link>
          </p>
          <h1>
            <CreditNoteMark kind={item.kind} />
            {INVOICE_KIND_LABELS[item.kind]} {item.number}{' '}
            <span className="title-date">del {formatDate(item.date)}</span> ·{' '}
            {item.counterpartyName}
          </h1>
          <p className="muted">
            {issued ? 'Netto da incassare' : 'Netto da pagare'}{' '}
            <strong>{formatMoney(item.netDue)}</strong>
            {Number(item.residual) !== 0 && Number(item.residual) !== Number(item.netDue) && (
              <> · resta {formatMoney(item.residual)}</>
            )}
            {item.nextDue && Number(item.residual) !== 0 && (
              <> · scadenza {formatDate(item.nextDue)}</>
            )}
            {item.subjectId ? (
              <>
                {' · '}
                <Link to={`/anagrafica/${item.subjectId}`}>apri il soggetto in Anagrafica</Link>
              </>
            ) : (
              ' · non in Anagrafica: si collega da sola quando c’è con la stessa partita IVA o codice fiscale'
            )}
          </p>
        </div>
        <div className="stack-tight">
          <div className="row">
            {item.selfBilling && <span className="pill self-billing">autofattura</span>}
            <InvoiceStatePill
              state={item.state}
              direction={item.direction}
              kind={item.kind}
              replaced={item.replaced}
              cancelled={item.cancelled}
              overdueDays={item.overdueDays}
            />
          </div>
          {!opening && !manual && <ElectronicInvoice invoice={item} />}
        </div>
      </div>

      <InvoiceCancellation invoice={item} onSaved={refresh} />

      {issued && item.kind === 'fattura' && !item.selfBilling && !item.cancelled && (
        <InvoiceAdvanceSection
          invoice={item}
          canWrite={canWrite}
          accountLabel={(code) => accountLabel(code)}
          onSearch={
            bankSearch
              ? () => {
                  setSearching(true);
                  document
                    .getElementById('invoice-settlements')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              : null
          }
        />
      )}

      <div className="cards">
        <div className="card">
          {manual ? (
            <div className="row between">
              <h3>Inserito a mano</h3>
              {canWrite && (
                <div className="row">
                  {!item.replaced && (
                    <Link className="button" to={`/tesoreria/fatture/${item.id}/modifica`}>
                      Correggi
                    </Link>
                  )}
                  {item.settlements.length === 0 &&
                    item.replaces.length === 0 &&
                    !item.proforma?.replacedBy.length && (
                      <button type="button" onClick={() => setDeleting(true)}>
                        Elimina
                      </button>
                    )}
                </div>
              )}
            </div>
          ) : (
            <h3>
              {opening
                ? 'Dalla situazione iniziale (mastrini), non si modifica qui'
                : 'Da Gestify, non si modificano qui'}
            </h3>
          )}
          {item.replaces.length > 0 && (
            <p className="notice small">
              Sostituisce la pro-forma{' '}
              {item.replaces.map((proforma, index) => (
                <span key={proforma.id}>
                  {index > 0 && ', '}
                  <Link to={`/tesoreria/fatture/${proforma.id}`}>{proforma.number}</Link> del{' '}
                  {formatDate(proforma.date)}
                </span>
              ))}
              : i pagamenti fatti sulla pro-forma sono passati qui.
            </p>
          )}
          <dl className="facts">
            <Fact label="Imponibile" value={formatMoney(item.taxable)} />
            <Fact
              label="IVA"
              value={
                <>
                  {formatMoney(item.vat)}
                  {item.splitPayment && (
                    <small className="muted">split payment: la versa il cliente</small>
                  )}
                </>
              }
            />
            <Fact label="Totale documento" value={formatMoney(item.total)} />
            {Number(item.withholding) !== 0 && (
              <Fact
                label="Ritenuta d’acconto"
                value={
                  <>
                    {formatMoney(item.withholding)}
                    <small className="muted">
                      {issued
                        ? 'la versa il cliente all’Erario'
                        : 'la versa K-Rev all’Erario con l’F24: non è una rata'}
                    </small>
                  </>
                }
              />
            )}
            <Fact
              label={issued ? 'Netto da incassare' : 'Netto da pagare'}
              value={<strong>{formatMoney(item.netDue)}</strong>}
            />
            <Fact label="Pagamento" value={item.paymentTerms ?? '—'} />
            <Fact
              label="Partita IVA / codice fiscale"
              value={
                [item.counterpartyVat, item.counterpartyTaxCode].filter(Boolean).join(' · ') || '—'
              }
            />
            {opening ? (
              <Fact
                label="Situazione iniziale"
                value={
                  <>
                    {item.notes ?? 'Partita aperta al 1° gennaio'}
                    <small className="muted">
                      <Link to="/tesoreria/situazione-iniziale">apri la situazione iniziale</Link>
                    </small>
                  </>
                }
              />
            ) : manual ? (
              <Fact label="Inserito il" value={formatTimestamp(item.importedAt)} />
            ) : (
              <Fact label="Letta da Gestify" value={formatTimestamp(item.importedAt)} />
            )}
          </dl>
        </div>
        <OurFields
          // A new form when the saved values change.
          key={[
            item.accountCode,
            item.costCenterCode,
            item.priorityCode,
            item.notes,
            item.reclass.code,
            item.reclass.manual,
            item.registryDefaults.accountCode,
            item.registryDefaults.costCenterCode,
            item.registryDefaults.priorityCode,
          ].join('|')}
          invoice={item}
          editable={canWrite}
          onSaved={refresh}
        />
      </div>

      {!opening && !manual && <Lines invoice={item} editable={canWrite} onSaved={refresh} />}

      <div className="card stack">
        <div className="row between">
          <h2>Scadenze</h2>
          <dl className="totals">
            <div>
              <dt>{issued ? 'Incassato' : 'Pagato'}</dt>
              <dd>{formatMoney(item.settled)}</dd>
            </div>
            <div className="grand">
              <dt>{issued ? 'Da incassare' : 'Da pagare'}</dt>
              <dd>{formatMoney(item.residual)}</dd>
            </div>
          </dl>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={dues} column="dueDate">
                  Scadenza
                </SortHeader>
                <SortHeader sort={dues} column="amount" numeric>
                  Importo
                </SortHeader>
                <SortHeader sort={dues} column="settled" numeric>
                  {issued ? 'Incassato' : 'Pagato'}
                </SortHeader>
                <SortHeader sort={dues} column="residual" numeric>
                  Resta
                </SortHeader>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {dues.rows.map((due, index) => (
                <tr key={`${due.dueDate}-${index}`}>
                  <td>{formatDate(due.dueDate)}</td>
                  <td className="numeric">{formatMoney(due.amount)}</td>
                  <td className="numeric">{formatMoney(due.settled)}</td>
                  <td className="numeric">{formatMoney(due.residual)}</td>
                  <td>
                    {item.replaced ? (
                      <span className="pill muted">Sostituita</span>
                    ) : Number(due.residual) === 0 ? (
                      <span className="pill ok">{issued ? 'Incassata' : 'Pagata'}</span>
                    ) : item.kind === 'nota_credito' ? (
                      // A credit note is used, never late (1.58).
                      <span className="pill warn">Da usare</span>
                    ) : due.overdueDays > 0 ? (
                      <span className="pill bad">Scaduta da {due.overdueDays} giorni</span>
                    ) : (
                      <span className="pill info">{issued ? 'Da incassare' : 'Da pagare'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {Number(item.duesDifference) !== 0 && (
          <p className="notice small">
            Le scadenze di Gestify non tornano con{' '}
            {issued ? 'il netto da incassare' : 'il netto da pagare'}: differenza di{' '}
            {formatMoney(item.duesDifference)}. Controlla le scadenze della fattura in Gestify.
          </p>
        )}
        <p className="muted small">
          {manual
            ? 'Le scadenze sono quelle inserite a mano; senza, il documento scade alla sua data. '
            : 'Le scadenze arrivano dallo scadenzario di Gestify; senza, la fattura scade alla sua data. '}
          Incassi e pagamenti coprono le scadenze in ordine, dalla più vecchia.
        </p>
      </div>

      <div className="card stack" id="invoice-settlements">
        <div className="row between">
          <h2>{issued ? 'Incassi' : 'Pagamenti'}</h2>
          <div className="row">
            {bankSearch && !searching && (
              <button type="button" className="primary" onClick={() => setSearching(true)}>
                Cerca in banca
              </button>
            )}
            {canWrite && Number(item.residual) !== 0 && (
              <button type="button" onClick={() => setSettling(true)}>
                Segna {what}
              </button>
            )}
            {canWrite && canClose && Number(item.residual) !== 0 && (
              <button type="button" onClick={() => setClosing(true)}>
                Chiudi senza movimento…
              </button>
            )}
          </div>
        </div>
        {searching && (
          <InvoiceBankPanel invoice={item} onClose={() => setSearching(false)} onSaved={refresh} />
        )}
        {canWrite && (
          <InvoiceSubjectPayments
            invoice={item}
            onDone={() => queryClient.invalidateQueries({ queryKey: treasuryKeys.all })}
          />
        )}
        {item.settlements.length === 0 ? (
          <p className="muted">Nessun {what} segnato.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={settlements} column="date">
                    Data
                  </SortHeader>
                  <SortHeader sort={settlements} column="amount" numeric>
                    Importo
                  </SortHeader>
                  <SortHeader sort={settlements} column="account">
                    Conto
                  </SortHeader>
                  <th>Nota</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {settlements.rows.map((settlement) => (
                  <tr key={settlement.id}>
                    <td>{formatDate(settlement.date)}</td>
                    <td className="numeric">{formatMoney(settlement.amount)}</td>
                    <td>{accountLabel(settlement.accountCode)}</td>
                    <td>
                      {settlement.creditLink ? (
                        <Link to={`/tesoreria/fatture/${settlement.creditLink.otherId}`}>
                          {item.kind === 'nota_credito'
                            ? `compensa la fattura ${settlement.creditLink.otherNumber}`
                            : `compensata con la nota di credito ${settlement.creditLink.otherNumber}`}
                        </Link>
                      ) : settlement.compensation ? (
                        <Link to={`/tesoreria/fatture/${settlement.compensation.otherId}`}>
                          compensata con la fattura {settlement.compensation.otherNumber}
                        </Link>
                      ) : settlement.movement ? (
                        <Link
                          to={movementPath(settlement.movement.accountCode, settlement.movement.id)}
                        >
                          dal movimento del {formatDate(settlement.movement.bookingDate)}
                        </Link>
                      ) : settlement.card ? (
                        // Undone from the spending (1.85): the link opens it.
                        <Link
                          to={`/tesoreria/carte?${new URLSearchParams({
                            spesa: settlement.card.movementId,
                            data: settlement.card.date,
                            carta: settlement.card.cardId,
                          }).toString()}`}
                        >
                          dalla spesa con carta •••• {settlement.card.last4} del{' '}
                          {formatDate(settlement.card.date)}
                        </Link>
                      ) : settlement.closure ? (
                        <>
                          <span className="pill info">senza movimento</span>{' '}
                          <strong>{CLOSURE_REASON_LABELS[settlement.closure.reason]}</strong>
                          {settlement.note && <span className="block">{settlement.note}</span>}
                          {settlement.closure.document && (
                            <button
                              type="button"
                              className="link block"
                              disabled={closureDocument.isPending}
                              onClick={() => closureDocument.mutate(settlement.id)}
                            >
                              {settlement.closure.document.name}
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {settlement.writeOff && <span className="pill info">abbuono</span>}{' '}
                          {settlement.note ?? ''}
                          {settlement.manual && (
                            <span className="pill warn">segnato a mano · non ancora in banca</span>
                          )}
                        </>
                      )}
                    </td>
                    {canWrite && (
                      <td className="numeric">
                        {!settlement.movement &&
                          !settlement.card &&
                          !settlement.creditLink &&
                          !settlement.compensation &&
                          (!settlement.closure || canClose) && (
                            <button
                              type="button"
                              className="link"
                              onClick={() => setRemoving(settlement.id)}
                            >
                              Togli
                            </button>
                          )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {canWrite && canWriteOff(item.residual) && (
          <div className="notice row between">
            <span>
              Residuo <strong>{formatMoney(item.residual)}</strong>: è una differenza di
              arrotondamento?
            </span>
            <button type="button" className="primary small" onClick={() => setWritingOff(true)}>
              Chiudi con un abbuono di {formatMoney(item.residual)}
            </button>
          </div>
        )}
        <p className="muted small">
          {issued ? 'Gli incassi' : 'I pagamenti'} arrivano dai movimenti delle banche
          {issued ? '' : ' e dalle spese con carta'}: con «Cerca in banca» si scelgono da qui.
          Segnati a mano, diventano quelli della banca quando il movimento è riconciliato.
        </p>
      </div>

      {(item.kind === 'nota_credito' || item.creditLinks.length > 0) && (
        <CreditLinks
          // A new form when the links or the candidates change.
          key={[item.residual, item.creditLinks.length, item.creditCandidates.length].join('|')}
          invoice={item}
          editable={canWrite}
          onSaved={refresh}
        />
      )}

      {(item.compensations.length > 0 || item.compensationCandidates.length > 0) && (
        <Compensations invoice={item} editable={canWrite} onSaved={refresh} />
      )}

      {/* The division of an invoice among commesse (2.03 issued, 2.04 received): managed here. */}
      {item.kind !== 'proforma' && <InvoiceJobsCard invoiceId={id} />}

      {item.advances.length > 0 && (
        <div className="card stack">
          <h2>Anticipi della banca</h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={advances} column="date">
                    Data
                  </SortHeader>
                  <SortHeader sort={advances} column="bank">
                    Banca
                  </SortHeader>
                  <SortHeader sort={advances} column="advanced" numeric>
                    Anticipato
                  </SortHeader>
                  <th>Restituito alla banca</th>
                  <SortHeader sort={advances} column="open" numeric>
                    Ancora dovuto
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {advances.rows.map((advance) => (
                  <tr key={advance.movementId}>
                    <td>{formatDate(advance.date)}</td>
                    <td>
                      <Link to={movementPath(advance.accountCode, advance.movementId)}>
                        {advance.accountCode}
                      </Link>
                      {advance.reference && (
                        <small className="muted block">{advance.reference}</small>
                      )}
                    </td>
                    <td className="numeric">{formatMoney(advance.advanced)}</td>
                    <td>
                      {advance.closures.length === 0
                        ? '—'
                        : advance.closures.map((closure) => (
                            <span key={closure.movementId} className="block">
                              {formatDate(closure.date)} · {ADVANCE_CLOSURE_LABELS[closure.kind]}
                              {closure.fromInvoice &&
                                ` della FT ${closure.fromInvoice.number}`} ·{' '}
                              {formatMoney(closure.amount)}
                            </span>
                          ))}
                    </td>
                    <td className="numeric">{formatMoney(advance.open)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">
            L’anticipo è un prestito della banca sulla fattura, non un incasso: la fattura si
            incassa quando paga il cliente. Vedi anche{' '}
            <Link to="/tesoreria/anticipi">Anticipi fatture</Link>.
          </p>
        </div>
      )}

      {item.proforma && (
        <ProformaPanel
          // A new form when the replacement changes.
          key={[item.proforma.replacedBy.length, item.proforma.candidates.length].join('|')}
          invoice={item}
          editable={canWrite}
          onSaved={refresh}
        />
      )}

      {manual && <ManualAttachment invoice={item} editable={canWrite} onSaved={refresh} />}

      <History id={id} />

      {closureDocument.error && <p className="error">{closureDocument.error.message}</p>}
      {closing && (
        <ClosureDialog
          invoice={item}
          onClose={() => setClosing(false)}
          onSaved={async (updated) => {
            setClosing(false);
            await refresh(updated);
          }}
        />
      )}
      {settling && (
        <SettlementDialog
          invoice={item}
          onClose={() => setSettling(false)}
          onSaved={async (updated) => {
            setSettling(false);
            await refresh(updated);
          }}
        />
      )}
      {deleting && (
        <ConfirmDialog
          title={`Eliminare ${INVOICE_KIND_LABELS[item.kind].toLowerCase()} ${item.number}?`}
          confirmLabel="Elimina"
          busy={removeDocument.isPending}
          error={removeDocument.error?.message}
          onConfirm={() => removeDocument.mutate()}
          onCancel={() => setDeleting(false)}
        >
          <p>Resta nel registro attività che era stato inserito e che è stato eliminato.</p>
        </ConfirmDialog>
      )}
      {writingOff && (
        <ConfirmDialog
          title={`Abbuono di ${formatMoney(item.residual)}`}
          confirmLabel="Chiudi con abbuono"
          busy={writeOff.isPending}
          error={writeOff.error?.message}
          onConfirm={() => writeOff.mutate()}
          onCancel={() => setWritingOff(false)}
        >
          <p>
            Chiude {item.kind === 'nota_credito' ? 'la nota di credito' : 'la fattura'} senza
            movimento di banca, con la data dell’ultimo {what}. Resta tra{' '}
            {issued ? 'gli incassi' : 'i pagamenti'} come «abbuono», non entra nei conti né nelle
            previsioni e si può togliere.
          </p>
        </ConfirmDialog>
      )}
      {removing && (
        <ConfirmDialog
          title={`Togliere questo ${what}?`}
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>Resta nel registro attività che era stato segnato e che è stato tolto.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/**
 * Credit notes linked to invoices (1.58). On a credit note: the invoices it
 * closed and, while something is left, those of the same subject it may close,
 * the one its electronic invoice cites already filled in. On an invoice: the
 * notes that lowered it. Undone from either side.
 */
function CreditLinks({
  invoice,
  editable,
  onSaved,
}: {
  invoice: InvoiceResponse;
  editable: boolean;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const note = invoice.kind === 'nota_credito';
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      invoice.creditCandidates
        .filter((candidate) => candidate.proposed)
        .map((candidate) => [candidate.id, candidate.proposed!]),
    ),
  );
  const [undoing, setUndoing] = useState<string | null>(null);
  // On a credit note the linked invoices, on an invoice the linked notes.
  const linked = useTableSort(
    note ? 'invoice.credit-links.invoices' : 'invoice.credit-links.notes',
    invoice.creditLinks,
    {
      number: (item) => item.other.number,
      date: (item) => item.other.date,
      amount: (item) => amountSort(item.amount),
    },
  );
  const candidates = useTableSort('invoice.credit-candidates', invoice.creditCandidates, {
    number: (candidate) => candidate.number,
    date: (candidate) => candidate.date,
    netDue: (candidate) => amountSort(candidate.netDue),
    residual: (candidate) => amountSort(candidate.residual),
  });
  const after = async (updated: InvoiceResponse) => {
    await onSaved(updated);
    // The other documents of the links changed too.
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
  };
  const links = Object.entries(amounts)
    .map(([invoiceId, value]) => ({ invoiceId, number: parseDecimal(value) }))
    .filter((entry) => entry.number !== null && entry.number > 0)
    .map((entry) => ({ invoiceId: entry.invoiceId, amount: entry.number!.toFixed(2) }));
  const used = links.reduce((sum, link) => sum + toCents(link.amount), 0);
  const left = Math.abs(toCents(invoice.residual)) - used;
  const error = creditLinkError(
    invoice.residual,
    links.map((link) => {
      const candidate = invoice.creditCandidates.find((item) => item.id === link.invoiceId)!;
      return { number: candidate.number, residual: candidate.residual, amount: link.amount };
    }),
  );
  const link = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/credit-links`, {
        method: 'POST',
        body: { links } satisfies CreditLinkRequest,
      }),
    onSuccess: after,
  });
  const undo = useMutation({
    mutationFn: (linkId: string) =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/credit-links/${linkId}`, {
        method: 'DELETE',
      }),
    onSuccess: async (updated) => {
      setUndoing(null);
      await after(updated);
    },
  });
  const toggle = (id: string, residual: string) =>
    setAmounts((current) => {
      if (id in current) {
        const rest = { ...current };
        delete rest[id];
        return rest;
      }
      // As much as the invoice and what is left of the note allow.
      const cents = Math.min(Math.abs(toCents(residual)), Math.max(left, 0));
      return { ...current, [id]: fromCents(cents) };
    });
  const open = note && Number(invoice.residual) !== 0;
  const party = invoice.direction === 'emessa' ? 'cliente' : 'fornitore';

  return (
    <div className="card stack">
      <div className="row between">
        <h2>{note ? 'Fatture collegate' : 'Note di credito collegate'}</h2>
        {open && (
          <dl className="totals">
            <div className="grand">
              <dt>Da usare</dt>
              <dd>{formatMoney(fromCents(Math.abs(toCents(invoice.residual))))}</dd>
            </div>
          </dl>
        )}
      </div>
      {invoice.creditLinks.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={linked} column="number">
                  {note ? 'Fattura' : 'Nota di credito'}
                </SortHeader>
                <SortHeader sort={linked} column="date">
                  Del
                </SortHeader>
                <SortHeader sort={linked} column="amount" numeric>
                  Tolto
                </SortHeader>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {linked.rows.map((item) => (
                <tr key={item.id} className={creditNoteRow(item.other)}>
                  <td>
                    <CreditNoteMark kind={item.other.kind} />
                    <Link to={`/tesoreria/fatture/${item.other.id}`}>{item.other.number}</Link>
                  </td>
                  <td>{formatDate(item.other.date)}</td>
                  <td className="numeric">{formatMoney(item.amount)}</td>
                  {editable && (
                    <td className="numeric">
                      <button type="button" className="link" onClick={() => setUndoing(item.id)}>
                        Annulla
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {note && !open && invoice.creditLinks.length === 0 && (
        <p className="muted">Nessuna fattura collegata: la nota è chiusa da un rimborso.</p>
      )}
      {open && invoice.creditCandidates.length === 0 && (
        <p className="muted">
          Nessuna fattura aperta dello stesso {party}: quello che resta si chiude con un rimborso in
          banca, o con una fattura che arriverà.
        </p>
      )}
      {open && invoice.creditOpening && (
        <p className="muted small">
          {invoice.creditOpening === 'non-fissata'
            ? 'Le fatture di prima del 1° gennaio non ci sono ancora: entrano quando si fissa la situazione iniziale in '
            : `Nella situazione iniziale fissata non ci sono partite di questo ${party}: se aveva fatture aperte al 1° gennaio, la sua situazione è da controllare in `}
          <Link to="/tesoreria/situazione-iniziale">Tesoreria › Situazione iniziale</Link>.
        </p>
      )}
      {open && editable && invoice.creditCandidates.length > 0 && (
        <>
          {!invoice.creditXmlRead && (
            <p className="notice small">
              Apri la fattura elettronica della nota (più sotto): se cita la fattura che corregge,
              la proposta si compila da sola.
            </p>
          )}
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={candidates} column="number">
                    Fattura
                  </SortHeader>
                  <SortHeader sort={candidates} column="date">
                    Data
                  </SortHeader>
                  <SortHeader sort={candidates} column="netDue" numeric>
                    Netto
                  </SortHeader>
                  <SortHeader sort={candidates} column="residual" numeric>
                    Residuo
                  </SortHeader>
                  <th className="numeric">Togli</th>
                  <th className="numeric">Resta sulla fattura</th>
                </tr>
              </thead>
              <tbody>
                {candidates.rows.map((candidate) => {
                  const checked = candidate.id in amounts;
                  const taken = checked ? (parseDecimal(amounts[candidate.id]!) ?? 0) : 0;
                  const rest = Math.abs(toCents(candidate.residual)) - Math.round(taken * 100);
                  return (
                    <tr key={candidate.id}>
                      <td>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(candidate.id, candidate.residual)}
                          />{' '}
                          <Link to={`/tesoreria/fatture/${candidate.id}`}>{candidate.number}</Link>
                        </label>
                        {candidate.origin === 'apertura' && (
                          <span className="pill info">situazione iniziale</span>
                        )}
                        {candidate.cited === 'xml' && (
                          <span className="pill info">citata nella fattura elettronica</span>
                        )}
                        {candidate.cited === 'testo' && (
                          <span className="pill info">citata nella descrizione</span>
                        )}
                      </td>
                      <td>{formatDate(candidate.date)}</td>
                      <td className="numeric">{formatMoney(candidate.netDue)}</td>
                      <td className="numeric">{formatMoney(candidate.residual)}</td>
                      <td className="numeric">
                        {checked ? (
                          <DecimalInput
                            aria-label={`Da togliere dalla fattura ${candidate.number}`}
                            value={amounts[candidate.id]!}
                            onChange={(value) =>
                              setAmounts((current) => ({ ...current, [candidate.id]: value }))
                            }
                            decimals={[2, 2]}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="numeric">{formatMoney(fromCents(rest))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="row between">
            <p className="muted small">
              Si toglie dal netto, IVA compresa, senza movimenti in banca. Della nota restano{' '}
              <strong>{formatMoney(fromCents(Math.max(left, 0)))}</strong>
              {left > 0 ? ': si usano dopo con un’altra fattura o con un rimborso.' : '.'}
            </p>
            <button
              type="button"
              className="primary"
              disabled={!links.length || Boolean(error) || link.isPending}
              onClick={() => link.mutate()}
            >
              {link.isPending ? 'Attendere…' : 'Conferma il collegamento'}
            </button>
          </div>
          {links.length > 0 && error && <p className="error">{error}</p>}
          {link.error && <p className="error">{link.error.message}</p>}
        </>
      )}
      {undoing && (
        <ConfirmDialog
          title="Annullare il collegamento?"
          confirmLabel="Annulla il collegamento"
          busy={undo.isPending}
          error={undo.error?.message}
          onConfirm={() => undo.mutate(undoing)}
          onCancel={() => setUndoing(null)}
        >
          <p>
            La fattura e la nota di credito tornano aperte per questo importo. Resta nel registro
            attività.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Commercial compensation (1.71): an issued and a received invoice of the same
 * subject close each other for the same amount, on the account Compensazioni.
 * The larger one stays open for the difference; undone from either side.
 */
function Compensations({
  invoice,
  editable,
  onSaved,
}: {
  invoice: InvoiceResponse;
  editable: boolean;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [undoing, setUndoing] = useState<string | null>(null);
  const after = async (updated: InvoiceResponse) => {
    await onSaved(updated);
    // The other invoice changed too.
    await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
  };
  const undo = useMutation({
    mutationFn: (compensationId: string) =>
      apiRequest<InvoiceResponse>(
        `/treasury/invoices/${invoice.id}/compensations/${compensationId}`,
        { method: 'DELETE' },
      ),
    onSuccess: async (updated) => {
      setUndoing(null);
      await after(updated);
    },
  });
  const otherKind = invoice.direction === 'emessa' ? 'passiva' : 'attiva';
  const compensations = useTableSort('invoice.compensations', invoice.compensations, {
    date: (item) => item.date,
    other: (item) => item.other.number,
    amount: (item) => amountSort(item.amount),
    note: (item) => item.note ?? null,
  });

  return (
    <div className="card stack">
      <div className="row between">
        <h2>Compensazioni</h2>
        {editable && invoice.compensationCandidates.length > 0 && (
          <button type="button" onClick={() => setAdding(true)}>
            Compensa con una fattura {otherKind}
          </button>
        )}
      </div>
      {invoice.compensations.length > 0 ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={compensations} column="date">
                  Data
                </SortHeader>
                <SortHeader sort={compensations} column="other">
                  Fattura {otherKind}
                </SortHeader>
                <SortHeader sort={compensations} column="amount" numeric>
                  Compensato
                </SortHeader>
                <SortHeader sort={compensations} column="note">
                  Nota
                </SortHeader>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {compensations.rows.map((item) => (
                <tr key={item.id}>
                  <td>{formatDate(item.date)}</td>
                  <td>
                    <Link to={`/tesoreria/fatture/${item.other.id}`}>{item.other.number}</Link>
                    <small className="muted block">del {formatDate(item.other.date)}</small>
                  </td>
                  <td className="numeric">{formatMoney(item.amount)}</td>
                  <td>{item.note ?? ''}</td>
                  {editable && (
                    <td className="numeric">
                      <button type="button" className="link" onClick={() => setUndoing(item.id)}>
                        Annulla
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">
          Lo stesso soggetto ha fatture {invoice.direction === 'emessa' ? 'passive' : 'attive'}{' '}
          aperte: si possono compensare con questa, senza movimenti in banca.
        </p>
      )}
      <p className="muted small">
        La compensazione chiude le due fatture per lo stesso importo, IVA compresa, sul conto
        Compensazioni. Se una delle due è più grande, la differenza resta aperta e si incassa o si
        paga in banca.
      </p>
      {adding && (
        <CompensationDialog
          invoice={invoice}
          onClose={() => setAdding(false)}
          onSaved={async (updated) => {
            setAdding(false);
            await after(updated);
          }}
        />
      )}
      {undoing && (
        <ConfirmDialog
          title="Annullare la compensazione?"
          confirmLabel="Annulla la compensazione"
          busy={undo.isPending}
          error={undo.error?.message}
          onConfirm={() => undo.mutate(undoing)}
          onCancel={() => setUndoing(null)}
        >
          <p>Le due fatture tornano aperte per questo importo. Resta nel registro attività.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function CompensationDialog({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: InvoiceResponse;
  onClose: () => void;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const issued = invoice.direction === 'emessa';
  const candidates = invoice.compensationCandidates;
  const [otherId, setOtherId] = useState(candidates[0]!.id);
  const chosen = candidates.find((item) => item.id === otherId)!;
  const [amount, setAmount] = useState(() =>
    compensationProposal(invoice.residual, candidates[0]!.residual),
  );
  const [date, setDate] = useState(italianToday());
  const [note, setNote] = useState('');
  const number = parseDecimal(amount);
  const value = number === null ? '0.00' : number.toFixed(2);
  const error = compensationError(
    [
      { number: invoice.number, residual: invoice.residual },
      { number: chosen.number, residual: chosen.residual },
    ],
    value,
  );
  const outcome = compensationOutcome(
    issued ? invoice.residual : chosen.residual,
    issued ? chosen.residual : invoice.residual,
    value,
  );
  const issuedNumber = issued ? invoice.number : chosen.number;
  const receivedNumber = issued ? chosen.number : invoice.number;
  const save = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/compensations`, {
        method: 'POST',
        body: { otherId, date, amount: value, note } satisfies CompensationRequest,
      }),
    onSuccess: onSaved,
  });
  return (
    <ConfirmDialog
      title={`Compensa la fattura ${invoice.number}`}
      confirmLabel="Compensa"
      busy={save.isPending}
      disabled={Boolean(error)}
      error={save.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      <Field label={issued ? 'Con la fattura passiva' : 'Con la fattura attiva'}>
        <select
          value={otherId}
          onChange={(event) => {
            const next = candidates.find((item) => item.id === event.target.value)!;
            setOtherId(next.id);
            setAmount(compensationProposal(invoice.residual, next.residual));
          }}
        >
          {candidates.map((item) => (
            <option key={item.id} value={item.id}>
              {item.number} del {formatDate(item.date)} · resta {formatMoney(item.residual)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Data della compensazione">
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
      </Field>
      <Field
        label="Importo compensato"
        hint={`Su questa fattura restano ${formatMoney(invoice.residual)}, sulla ${chosen.number} ${formatMoney(chosen.residual)}.`}
      >
        <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
      </Field>
      <Field label="Nota">
        <input
          type="text"
          maxLength={200}
          value={note}
          placeholder="Ad esempio: accordo del…"
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>
      {error ? (
        <p className="error">{error}</p>
      ) : (
        <p className="notice small">
          {outcome.kind === 'totale'
            ? `Compensazione totale: si chiudono sia la fattura attiva ${issuedNumber} sia la passiva ${receivedNumber}.`
            : outcome.kind === 'resta-attiva'
              ? `La fattura passiva ${receivedNumber} si chiude; sulla fattura attiva ${issuedNumber} restano ${formatMoney(outcome.issuedLeft)} da incassare in banca.`
              : outcome.kind === 'resta-passiva'
                ? `La fattura attiva ${issuedNumber} si chiude; sulla fattura passiva ${receivedNumber} restano ${formatMoney(outcome.receivedLeft)} da pagare in banca.`
                : `Compensazione parziale: sulla fattura attiva ${issuedNumber} restano ${formatMoney(outcome.issuedLeft)} da incassare, sulla passiva ${receivedNumber} ${formatMoney(outcome.receivedLeft)} da pagare.`}
        </p>
      )}
    </ConfirmDialog>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function OptionSelect({
  value,
  options,
  onChange,
  disabled,
  inherited = null,
}: {
  value: string;
  options: TreasuryOption[];
  onChange: (value: string) => void;
  disabled: boolean;
  /** The value of the Anagrafica the empty choice stands for (1.74). */
  inherited?: string | null;
}) {
  const label = inherited
    ? (options.find((option) => option.code === inherited)?.label ?? inherited)
    : null;
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
      <option value="">{label ? `${label} (dall’anagrafica)` : '—'}</option>
      {options.map((option) => (
        <option key={option.code} value={option.code}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

type OurField = 'accountCode' | 'costCenterCode' | 'priorityCode';

const OUR_FIELD_LABELS: Record<OurField | 'reclassCode', string> = {
  accountCode: 'Conto previsto',
  costCenterCode: 'Centro di costo',
  priorityCode: 'Priorità di pagamento',
  reclassCode: 'Voce riclassificata',
};

/**
 * How K-Rev handles the invoice: data of K-Management only, never sent to or
 * overwritten by Gestify. Without a choice of its own a field follows the
 * role of the subject in the Anagrafica (1.74); a change goes to this invoice
 * only, or to the Anagrafica too, for the invoices that follow it.
 */
function OurFields({
  invoice,
  editable,
  onSaved,
}: {
  invoice: InvoiceResponse;
  editable: boolean;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const initial = () => ({
    accountCode: invoice.accountCode ?? '',
    costCenterCode: invoice.costCenterCode ?? '',
    priorityCode: invoice.priorityCode ?? '',
    notes: invoice.notes ?? '',
    reclassCode: invoice.reclass.code,
    reclassManual: invoice.reclass.manual,
    intercompany: invoice.intercompany,
  });
  const [form, setForm] = useState(initial);
  const [asking, setAsking] = useState(false);
  const [registry, setRegistry] = useState(false);
  const defaults = invoice.registryDefaults;
  const party = invoice.direction === 'emessa' ? 'cliente' : 'fornitore';
  const save = useMutation({
    mutationFn: (toRegistry: boolean) =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}`, {
        method: 'PATCH',
        body: {
          accountCode: form.accountCode || null,
          costCenterCode: form.costCenterCode || null,
          priorityCode: form.priorityCode || null,
          notes: form.notes,
          // By hand, or back to the item of the registry (1.44).
          reclassCode: form.reclassManual ? form.reclassCode : null,
          intercompany: form.intercompany,
          registry: toRegistry,
        },
      }),
    onSuccess: async (updated) => {
      setAsking(false);
      await onSaved(updated);
    },
  });
  const start = initial();
  const changed = JSON.stringify(form) !== JSON.stringify(start);
  // What may go to the Anagrafica too: a value chosen, different from before.
  const toRegistry = [
    ...(['accountCode', 'costCenterCode', 'priorityCode'] as const).filter(
      (key) => form[key] && form[key] !== start[key],
    ),
    ...(form.reclassManual && form.reclassCode && form.reclassCode !== start.reclassCode
      ? (['reclassCode'] as const)
      : []),
  ];
  const submit = () => {
    if (defaults.roleCode && toRegistry.length > 0) {
      setRegistry(false);
      setAsking(true);
    } else save.mutate(false);
  };
  const set = (key: OurField | 'notes') => (value: string) => setForm({ ...form, [key]: value });
  const fromRegistry = !form.reclassManual;
  const followHint = (key: OurField, purpose: string) =>
    `${purpose}${form[key] ? ' Scelto per questa fattura.' : defaults[key] ? ` Senza scelta vale quello dell’anagrafica del ${party}.` : ''}`;
  return (
    <div className="card stack">
      <div>
        <h3>Come la gestisce K-Rev</h3>
        <p className="muted small">
          Dati solo di K-Management: non tornano in Gestify e la lettura da Gestify non li cambia.
          Ogni modifica va nello storico.
        </p>
      </div>
      <Field
        label="Voce riclassificata"
        hint={
          <>
            Dove va la fattura nel Controllo di gestione.{' '}
            {fromRegistry ? (
              form.reclassCode === invoice.reclass.code && !invoice.reclass.manual ? (
                form.reclassCode ? (
                  `Dall’anagrafica del ${party}.`
                ) : (
                  <span className="warn-strong">
                    Il {party} non ne ha una in Anagrafica: sceglila qui, per questa fattura o anche
                    in Anagrafica.
                  </span>
                )
              ) : (
                'Torna a quella dell’anagrafica al salvataggio.'
              )
            ) : (
              <>
                Scelta a mano.{' '}
                {editable && (
                  <button
                    type="button"
                    className="link"
                    onClick={() => setForm({ ...form, reclassManual: false, reclassCode: null })}
                  >
                    Torna a quella dell’anagrafica
                  </button>
                )}
              </>
            )}
          </>
        }
      >
        <ReclassPicker
          value={form.reclassCode}
          choices={invoice.options.reclass}
          disabled={!editable}
          noneLabel="— come l’anagrafica"
          shownWhenEmpty={fromRegistry ? `Nessuna voce: il ${party} non ne ha in Anagrafica` : '—'}
          onChange={(code) => setForm({ ...form, reclassCode: code, reclassManual: code !== null })}
        />
      </Field>
      <Field
        label="Conto previsto"
        hint={followHint(
          'accountCode',
          `Da dove si ${invoice.direction === 'emessa' ? 'incasserà' : 'pagherà'}: proposto quando si segna a mano; servirà a Scadenzario e Previsioni.`,
        )}
      >
        <OptionSelect
          value={form.accountCode}
          options={invoice.options.accounts}
          onChange={set('accountCode')}
          disabled={!editable}
          inherited={defaults.accountCode}
        />
      </Field>
      {invoice.direction === 'ricevuta' && (
        <Field
          label="Priorità di pagamento"
          hint={followHint('priorityCode', 'Servirà allo Scadenzario per l’ordine dei pagamenti.')}
        >
          <OptionSelect
            value={form.priorityCode}
            options={invoice.options.priorities}
            onChange={set('priorityCode')}
            disabled={!editable}
            inherited={defaults.priorityCode}
          />
        </Field>
      )}
      <Field
        label="Centro di costo"
        hint={followHint('costCenterCode', 'Per il Controllo di gestione.')}
      >
        <OptionSelect
          value={form.costCenterCode}
          options={invoice.options.costCenters}
          onChange={set('costCenterCode')}
          disabled={!editable}
          inherited={defaults.costCenterCode}
        />
      </Field>
      <Field label="Note">
        <textarea
          rows={3}
          value={form.notes}
          onChange={(event) => set('notes')(event.target.value)}
          disabled={!editable}
        />
      </Field>
      {(invoice.linkedCompany || invoice.intercompany) && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.intercompany}
            disabled={!editable}
            onChange={(event) => setForm({ ...form, intercompany: event.target.checked })}
          />
          Fattura intergruppo: fuori dai report economici, con il totale sempre indicato
        </label>
      )}
      {save.error && !asking && <p className="error">{save.error.message}</p>}
      {editable && (
        <div className="actions">
          <button
            type="button"
            className="primary"
            disabled={!changed || save.isPending}
            onClick={submit}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      )}
      {asking && (
        <ConfirmDialog
          title="Salvare le modifiche"
          confirmLabel="Salva"
          busy={save.isPending}
          error={save.error?.message}
          onConfirm={() => save.mutate(registry)}
          onCancel={() => setAsking(false)}
        >
          <p className="small">
            Hai cambiato{' '}
            {toRegistry.map((key, index) => (
              <span key={key}>
                {index > 0 && (index === toRegistry.length - 1 ? ' e ' : ', ')}
                <strong>{OUR_FIELD_LABELS[key]}</strong>
              </span>
            ))}
            .
          </p>
          <label className="checkbox choice">
            <input type="radio" checked={!registry} onChange={() => setRegistry(false)} />
            <span>
              <strong>Solo questa fattura</strong>
              <small className="muted block">
                L’anagrafica non cambia; questa fattura tiene i valori scelti.
              </small>
            </span>
          </label>
          <label className="checkbox choice">
            <input type="radio" checked={registry} onChange={() => setRegistry(true)} />
            <span>
              <strong>Anche in Anagrafica</strong> (ruolo{' '}
              {defaults.roleCode === 'C' ? 'Cliente' : 'Fornitore'} di {invoice.counterpartyName})
              <small className="muted block">
                Diventano i valori del {party}: li prendono questa fattura e le altre sue che
                seguono l’anagrafica; quelle cambiate a mano restano come sono.
              </small>
            </span>
          </label>
        </ConfirmDialog>
      )}
    </div>
  );
}

function SettlementDialog({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: InvoiceResponse;
  onClose: () => void;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const issued = invoice.direction === 'emessa';
  const [date, setDate] = useState(italianToday());
  const [amount, setAmount] = useState(invoice.residual);
  const [accountCode, setAccountCode] = useState(
    invoice.accountCode ?? invoice.registryDefaults.accountCode ?? '',
  );
  const [note, setNote] = useState('');
  const number = parseDecimal(amount);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/settlements`, {
        method: 'POST',
        body: { date, amount: number!.toFixed(2), accountCode: accountCode || null, note },
      }),
    onSuccess: onSaved,
  });
  const partial = number !== null && Math.abs(number) < Math.abs(Number(invoice.residual));
  return (
    <ConfirmDialog
      title={issued ? 'Segna un incasso' : 'Segna un pagamento'}
      confirmLabel={issued ? 'Segna incassato' : 'Segna pagato'}
      busy={save.isPending}
      disabled={number === null || number === 0}
      error={save.error?.message}
      onConfirm={() => save.mutate()}
      onCancel={onClose}
    >
      <Field label="Data">
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
      </Field>
      <Field
        label="Importo"
        hint={
          partial
            ? `Meno di quanto resta (${formatMoney(invoice.residual)}): ${issued ? 'incasso' : 'pagamento'} parziale.`
            : invoice.kind === 'nota_credito'
              ? 'Nota di credito: l’importo è negativo.'
              : undefined
        }
      >
        <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
      </Field>
      <Field label="Conto">
        <OptionSelect
          value={accountCode}
          options={invoice.options.accounts}
          onChange={setAccountCode}
          disabled={false}
        />
      </Field>
      <Field label="Nota">
        <input
          type="text"
          maxLength={300}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Field>
      <p className="muted small">
        Per ora si segna a mano. Con «Banche e movimenti» arriverà dall’estratto conto e questo
        passo diventerà una conferma.
      </p>
    </ConfirmDialog>
  );
}

/**
 * The electronic invoice (07/10/2026): read from Gestify the first time,
 * kept in K-Management, then opened as a page to print or save as PDF.
 */
function ElectronicInvoice({ invoice }: { invoice: InvoiceResponse }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const gestify = useGestifyBrowser();
  const canWrite = useCan('tesoreria.write');
  const [connecting, setConnecting] = useState(false);
  const page = `/tesoreria/fatture/${invoice.id}/fattura-elettronica`;
  const xmlKey = [...treasuryKeys.invoice(invoice.id), 'xml'];
  const stored = useQuery({
    queryKey: xmlKey,
    queryFn: () => apiRequest<InvoiceXmlResponse>(`/treasury/invoices/${invoice.id}/xml`),
    retry: false,
  });
  const fetchXml = useMutation({
    mutationFn: async () => {
      let saved: InvoiceXmlResponse | null = null;
      const read = await readInvoiceXml(
        gestify,
        invoice.direction,
        invoice.gestifyId,
        async (url) => {
          saved = await apiRequest<InvoiceXmlResponse>(
            `/treasury/invoices/${invoice.id}/xml/from-link`,
            { method: 'POST', body: { url } },
          );
          return saved.xml;
        },
      );
      if (read.stored && saved) return saved;
      return apiRequest<InvoiceXmlResponse>(`/treasury/invoices/${invoice.id}/xml`, {
        method: 'PUT',
        body: { xml: read.xml },
      });
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(xmlKey, saved);
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.history(invoice.id) });
      // A credit note proposes the invoice its electronic invoice cites (1.58).
      await queryClient.invalidateQueries({
        queryKey: treasuryKeys.invoice(invoice.id),
        exact: true,
      });
      navigate(page);
    },
  });
  const open = () => {
    if (stored.data) navigate(page);
    else if (!gestify.connectedAs) setConnecting(true);
    else fetchXml.mutate();
  };
  if (!stored.data && !canWrite) return null;
  return (
    <div className="stack-tight">
      <button
        type="button"
        onClick={open}
        disabled={fetchXml.isPending || stored.isPending}
        title={
          stored.data
            ? `La fattura come la vede l’Agenzia delle Entrate, letta da Gestify il ${formatTimestamp(stored.data.storedAt)}: da stampare, salvare in PDF o scaricare in XML.`
            : 'La prima volta si legge da Gestify (serve essere collegati); poi resta in K-Management.'
        }
      >
        {fetchXml.isPending ? 'Lettura da Gestify…' : 'Fattura elettronica (PDF)'}
      </button>
      {fetchXml.error && <p className="error small">{fetchXml.error.message}</p>}
      {connecting && (
        <GestifyConnectDialog
          onClose={() => setConnecting(false)}
          onConnected={() => {
            setConnecting(false);
            fetchXml.mutate();
          }}
        />
      )}
    </div>
  );
}

/**
 * The lines of the invoice as Gestify has them, read-only, each with the item
 * of the plan Base: the one of the invoice unless chosen for the line (1.44).
 */
function Lines({
  invoice,
  editable,
  onSaved,
}: {
  invoice: InvoiceResponse;
  editable: boolean;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const save = useMutation({
    mutationFn: ({ position, code }: { position: number; code: string | null }) =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/lines/${position}/reclass`, {
        method: 'PUT',
        body: { code },
      }),
    onSuccess: onSaved,
  });
  const [splitting, setSplitting] = useState<number | null>(null);
  const invoiceItem = invoice.options.reclass.find((item) => item.code === invoice.reclass.code);
  const total = invoice.lines.reduce((sum, line) => sum + toCents(line.amount), 0);
  const splitLine = invoice.lines.find((line) => line.position === splitting);
  return (
    <details className="card stack collapsible">
      <summary>
        <h2>
          Righe{' '}
          <span className="muted small summary-note">
            {invoice.detailsReadAt === null
              ? '· non ancora lette'
              : invoice.lines.length === 0
                ? '· nessuna riga'
                : `· ${invoice.lines.length === 1 ? '1 riga' : `${invoice.lines.length} righe`} · ${formatMoney(fromCents(total))}${invoiceItem ? ` · voce ${invoiceItem.code} ${invoiceItem.description}` : ''}`}
          </span>
        </h2>
      </summary>
      {invoice.detailsReadAt === null ? (
        <p className="muted">
          Righe non ancora lette: arrivano con il prossimo «Aggiorna da Gestify».
        </p>
      ) : invoice.lines.length === 0 ? (
        <p className="muted">Gestify non ha righe per questo documento.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Descrizione</th>
                <th className="numeric">Quantità</th>
                <th className="numeric">Prezzo</th>
                <th className="numeric">Sconto %</th>
                <th>IVA</th>
                <th className="numeric">Importo</th>
                <th>Voce</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line, index) => (
                <tr key={index}>
                  <td className="pre-line">{line.description}</td>
                  <td className="numeric">
                    {line.quantity === null ? '' : formatNumber(line.quantity, 3)}
                  </td>
                  <td className="numeric">
                    {line.unitPrice === null ? '' : formatMoney(line.unitPrice)}
                  </td>
                  <td className="numeric">{line.discount ?? ''}</td>
                  <td>{line.vat ?? ''}</td>
                  <td className="numeric">{formatMoney(line.amount)}</td>
                  <td className="line-reclass">
                    {line.reclassParts.length > 0 ? (
                      <span className="small">
                        {line.reclassParts
                          .map((part) => `${part.code} · ${formatMoney(part.amount)}`)
                          .join(' + ')}
                      </span>
                    ) : (
                      <ReclassPicker
                        value={line.reclassCode}
                        choices={invoice.options.reclass}
                        disabled={!editable || save.isPending}
                        noneLabel="— come la fattura"
                        shownWhenEmpty={
                          invoiceItem
                            ? `${invoiceItem.code} · ${invoiceItem.description} (dalla fattura)`
                            : 'come la fattura'
                        }
                        onChange={(code) => save.mutate({ position: line.position, code })}
                      />
                    )}
                    {editable && (
                      <button
                        type="button"
                        className="link small block"
                        onClick={() => setSplitting(line.position)}
                      >
                        {line.reclassParts.length > 0
                          ? 'Cambia la divisione'
                          : 'Dividi su più voci'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {invoice.detailsReadAt && (
        <p className="muted small">
          Da Gestify, in sola lettura: lette il {formatTimestamp(invoice.detailsReadAt)}.
        </p>
      )}
      {splitLine && (
        <LineSplitDialog
          invoice={invoice}
          line={splitLine}
          onSaved={onSaved}
          onClose={() => setSplitting(null)}
        />
      )}
    </details>
  );
}

function History({ id }: { id: string }) {
  const history = useQuery({
    queryKey: treasuryKeys.history(id),
    queryFn: () => apiRequest<AuditEventResponse[]>(`/treasury/invoices/${id}/history`),
  });
  const last = history.data?.[0];
  return (
    <details className="card stack collapsible">
      <summary>
        <h2>
          Storico{' '}
          {history.data && (
            <span className="muted small summary-note">
              {history.data.length === 0
                ? '· nessuna modifica'
                : `· ${history.data.length === 1 ? '1 modifica' : `${history.data.length} modifiche`}, l’ultima il ${formatTimestamp(last!.occurredAt)}`}
            </span>
          )}
        </h2>
      </summary>
      {history.data?.length === 0 && <p className="muted">Nessuna modifica in K-Management.</p>}
      {history.data && history.data.length > 0 && (
        <ul className="plain-list">
          {history.data.map((event) => (
            <li key={event.id}>
              <span className="muted small">
                {formatTimestamp(event.occurredAt)} · {event.actorEmail ?? 'sistema'} ·{' '}
                {AUDIT_ACTION_LABELS[event.action]}
              </span>
              <div>{event.summary}</div>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
